import {
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  HashIcon,
  InfoIcon,
  DistributionGroupAvatarIcon,
  InboxSlashIcon,
  UserEmailCircleIcon,
  ExternalIcon,
  PersonOutlineIcon,
  BuildingIcon,
  AtSignIcon,
  MailArrowIcon,
  ThreadsIcon,
  InboxOutlineIcon,
} from "modules/icons";
import { useAuth } from "modules/providers/auth-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useTags } from "modules/providers/tags-provider";
import { useUsers } from "modules/providers/users-provider";
import EmailAddress from "modules/_shared/email-address";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import UserAvatar from "modules/_shared/user-avatar";
import UserName from "modules/_shared/user-name";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { Fragment, memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import {
  getUserFullName,
  sortUsersByEmailAddress,
  sortUsersByLastName,
} from "modules/utils/Functions";
import NewMessageThreadButton from "modules/_shared/buttons/new-message-thread-button";
import VideoChatButton from "modules/_shared/buttons/video-chat-button";
import UserDistributionGroupToggleStarButton from "modules/_shared/buttons/user-distribution-group-toggle-star-button";
import HeaderRow from "./profiles/header-row";
import InfoRow from "./profiles/info-row";
import { CALLOUT_KEY, TAG_GROUP_THREADS_PATH } from "modules/utils/Constants";
import { MessageThreadType, SearchFilterType } from "gql/graphql";
import { useRouter } from "next/router";
import useSearchFilter from "modules/hooks/use-search-filter";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  distributionGroupId: string;
}

const DistributionGroupProfileDrawer = ({ distributionGroupId }: IProps) => {
  const { me } = useAuth();
  const { distributionGroupsById } = useDistributionGroups();
  const { getUsersById } = useUsers();
  const { getTagsById } = useTags();
  const { push } = useRouter();
  const { closeDrawer } = useOverlay();
  const { getUrl } = useSearchFilter();

  const distributionGroup = useMemo(
    () => distributionGroupsById[distributionGroupId],
    [distributionGroupId, distributionGroupsById],
  );

  const users = useMemo(
    () =>
      getUsersById(distributionGroup?.userIdList || []).filter(
        (obj) => !!obj && obj.active === true,
      ) ?? [],
    [distributionGroup?.userIdList, getUsersById],
  );

  const emailUsers = useMemo(
    () => getUsersById(distributionGroup?.emailUserIdList || []),
    [distributionGroup?.emailUserIdList, getUsersById],
  );

  const tags = useMemo(
    () =>
      getTagsById(distributionGroup?.tagIdList || []).filter(
        (tag) => tag.active,
      ),
    [distributionGroup?.tagIdList, getTagsById],
  );

  const onViewThreadsClicked = useCallback(() => {
    if (!distributionGroup) return null;
    closeDrawer();
    push(
      getUrl(
        TAG_GROUP_THREADS_PATH,
        undefined,
        ...[
          {
            type: SearchFilterType.TypeList,
            stringListValue: [MessageThreadType.Thread],
          },
          {
            type: SearchFilterType.DistributionGroupIdList,
            objectIdListValue: [distributionGroup?.id],
          },
        ],
      ),
    );
  }, [closeDrawer, distributionGroup, getUrl, push]);

  if (!distributionGroup || !me) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader pb={0}>
        <Center fontWeight="bold">Group Details</Center>
        <Divider pt={5} />
      </DrawerHeader>
      <DrawerBody>
        <Stack py={5} spacing={0} w="full">
          <Center>
            <VStack>
              <DistributionGroupAvatarIcon boxSize={100} fill={colors.blue} />
              {!!distributionGroup.emailUserIdList.length && (
                <ExternalIcon fill={colors.transparent} />
              )}
              <HStack spacing={0.25} pl={8}>
                <Text fontSize={28} fontWeight={700}>
                  {distributionGroup.name}
                </Text>
                <UserDistributionGroupToggleStarButton
                  userOrGroupId={distributionGroup.id}
                />
              </HStack>
              <HStack>
                <VideoChatButton
                  userIds={distributionGroup?.userIdList}
                  hasText
                  textAlignment="inline"
                />
                <NewMessageThreadButton
                  distributionGroupId={distributionGroup.id}
                />
              </HStack>
            </VStack>
          </Center>
          <Stack spacing={6} w="full">
            <Stack
              spacing={0}
              w="full"
              divider={<Divider borderColor={colors.black} pt={2} />}
            >
              <HeaderRow
                icon={<BuildingIcon boxSize={5} />}
                text="Synco Info"
              />
              <Stack
                spacing={2}
                w="full"
                pt={2}
                px={2}
                divider={<Divider borderColor={colors.gray0} />}
              >
                <InfoRow
                  icon={<AtSignIcon boxSize={4} fill={colors.gray30} />}
                  content={`${CALLOUT_KEY}${distributionGroup.name}`}
                />
                <InfoRow
                  icon={<MailArrowIcon boxSize={4} fill={colors.gray30} />}
                  content={
                    <EmailAddress id={distributionGroup.id} fontWeight={700} />
                  }
                />
                <InfoRow
                  icon={<ThreadsIcon boxSize={4} fill={colors.gray30} />}
                  content={
                    <Button
                      variant="link"
                      color={colors.blue}
                      fontWeight={600}
                      fontSize={14}
                      onClick={onViewThreadsClicked}
                    >
                      View Threads
                    </Button>
                  }
                />
              </Stack>
            </Stack>

            <Stack
              spacing={0}
              w="full"
              divider={<Divider borderColor={colors.black} pt={2} />}
            >
              <HeaderRow icon={<InfoIcon boxSize={5} />} text="Group Info" />
              <Stack spacing={2} w="full" pt={2} px={2}>
                <VStack w="full">
                  <Text fontWeight={700} fontSize={12} pt={2} w="full">
                    Tag(s)
                  </Text>
                  <Wrap pt={0.5} w="full">
                    {tags.map((tag) => (
                      <WrapItem key={tag.id}>
                        <HStack
                          spacing={1}
                          paddingX={2}
                          borderRadius={16}
                          borderWidth={1}
                          fontSize={13}
                          borderColor={colors.grayPrimary}
                          color={colors.blue}
                        >
                          <HashIcon boxSize="11px" fill={colors.blue} />
                          <Text
                            color={colors.blue}
                          >{` ${tag.displayName}`}</Text>
                        </HStack>
                      </WrapItem>
                    ))}
                  </Wrap>
                </VStack>
                <VStack w="full">
                  <Text fontWeight={700} fontSize={12} pt={2} w="full">
                    Description
                  </Text>
                  {distributionGroup.description ? (
                    <Text fontSize={12} color={colors.gray50} w="full">
                      {distributionGroup.description}
                    </Text>
                  ) : (
                    <Text
                      fontSize={12}
                      fontStyle="italic"
                      color={colors.gray50}
                      w="full"
                    >
                      no description
                    </Text>
                  )}
                </VStack>
              </Stack>
            </Stack>

            <Stack
              spacing={0}
              w="full"
              divider={<Divider borderColor={colors.black} pt={2} />}
            >
              <HeaderRow
                icon={<PersonOutlineIcon boxSize={5} />}
                text={`Members (${users.length + emailUsers.length})`}
              />
              <Stack spacing={2} w="full" pt={2} px={2} divider={<Divider />}>
                {users.sort(sortUsersByLastName).map((user) => (
                  <HStack w="full" minH={8} key={user.id}>
                    <UserAvatar
                      userId={user.id}
                      size="xs"
                      displayOnlineIndicator={false}
                    />
                    <Text>
                      <UserName userId={user.id} />
                    </Text>
                    <Spacer />
                    {distributionGroup.notificationUserIdList.includes(
                      user.id,
                    ) && (
                      <SyncoTooltip label="Group messages sent to Threads Inbox">
                        <InboxOutlineIcon fill={colors.grayMain} boxSize={6} />
                      </SyncoTooltip>
                    )}
                    {!distributionGroup.notificationUserIdList.includes(
                      user.id,
                    ) && (
                      <SyncoTooltip label="Group messages not sent to Threads Inbox">
                        <InboxSlashIcon fill={colors.gray30} boxSize={6} />
                      </SyncoTooltip>
                    )}
                  </HStack>
                ))}
                {emailUsers.length > 0 && (
                  <Stack pt={4} spacing={0} w="full">
                    <Text fontSize="xs" color={colors.grayMain} pb={2}>
                      EXTERNAL CONTACTS
                    </Text>
                    <Stack divider={<Divider />} spacing={1} w="full">
                      {emailUsers
                        .sort(sortUsersByEmailAddress)
                        .map((emailUser) => (
                          <React.Fragment key={emailUser.id}>
                            <HStack>
                              <UserEmailCircleIcon
                                boxSize={6}
                                fill={colors.white}
                              />
                              <Stack spacing={0}>
                                {emailUser.firstName && (
                                  <Text color={colors.gray50} fontSize={14}>
                                    {getUserFullName(emailUser)}
                                  </Text>
                                )}
                                <Text fontSize={14} color={colors.gray50}>
                                  {emailUser.email}
                                </Text>
                              </Stack>
                            </HStack>
                          </React.Fragment>
                        ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(DistributionGroupProfileDrawer);
